import React, { useState, useEffect, useMemo } from "react";
import {
  ThemeProvider,
  createTheme,
  alpha,
  useTheme,
} from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { useRecoilValue } from "recoil";
import {
  backgroundTypeState,
  backgroundOpacityState,
  chatSidebarVisibleState,
} from "../global-state";

/**
 * Get background style based on background type
 * @param {Object} theme - MUI theme object
 * @param {string} backgroundType - Type of background (default, sky, aurora, etc.)
 * @returns {string} Background CSS value
 */
// Gradient backgrounds - stored as constants, not in theme.palette
const gradientBackgrounds = {
  sky: {
    light:
      "linear-gradient(135deg, rgb(195, 215, 235) 0%, rgb(205, 225, 240) 25%, rgb(185, 210, 245) 50%, rgb(200, 220, 245) 75%, rgb(190, 215, 240) 100%)",
    dark: "linear-gradient(135deg, rgb(35, 55, 75) 0%, rgb(40, 60, 80) 25%, rgb(30, 50, 85) 50%, rgb(45, 65, 85) 75%, rgb(35, 60, 80) 100%)",
  },
  aurora: {
    light:
      "linear-gradient(135deg, rgb(170, 221, 216) 0%, rgb(191, 228, 191) 25%, rgb(214, 222, 223) 50%, rgb(211, 156, 206) 75%, rgb(166, 211, 203) 100%)",
    dark: "linear-gradient(135deg, rgb(42, 98, 93) 0%, rgb(70, 103, 70) 25%, rgb(82, 89, 98) 50%, rgb(85, 50, 94) 75%, rgb(45, 93, 91) 100%)",
  },
  coral: {
    light:
      "linear-gradient(135deg, rgb(240, 176, 150) 0%, rgb(240, 196, 204) 25%, rgb(240, 195, 170) 50%, rgb(240, 169, 203) 75%, rgb(240, 224, 209) 100%)",
    dark: "linear-gradient(135deg, rgb(84, 52, 40) 0%, rgb(84, 66, 68) 25%, rgb(84, 60, 51) 50%, rgb(84, 46, 65) 75%, rgb(84, 75, 66) 100%)",
  },
  lavender: {
    light:
      "linear-gradient(135deg, rgb(232, 216, 232) 0%, rgb(237, 237, 240) 25%, rgb(235, 230, 235) 50%, rgb(227, 211, 230) 75%, rgb(230, 232, 237) 100%)",
    dark: "linear-gradient(135deg, rgb(42, 36, 42) 0%, rgb(43, 43, 45) 25%, rgb(42, 39, 42) 50%, rgb(35, 31, 42) 75%, rgb(37, 42, 43) 100%)",
  },
  mint: {
    light:
      "linear-gradient(135deg, rgb(223, 238, 223) 0%, rgb(227, 236, 236) 25%, rgb(222, 236, 222) 50%, rgb(215, 228, 225) 75%, rgb(209, 229, 227) 100%)",
    dark: "linear-gradient(135deg, rgb(43, 58, 43) 0%, rgb(46, 56, 56) 25%, rgb(42, 56, 42) 50%, rgb(35, 51, 46) 75%, rgb(30, 54, 51) 100%)",
  },
  rose: {
    light:
      "linear-gradient(135deg, rgb(240, 212, 218) 0%, rgb(240, 215, 221) 25%, rgb(240, 199, 171) 50%, rgb(240, 188, 163) 75%, rgb(240, 231, 230) 100%)",
    dark: "linear-gradient(135deg, rgb(71, 56, 59) 0%, rgb(71, 58, 61) 25%, rgb(71, 52, 44) 50%, rgb(70, 46, 43) 75%, rgb(71, 66, 65) 100%)",
  },
  galaxy: {
    light:
      "linear-gradient(135deg, rgb(157, 157, 174) 0%, rgb(185, 153, 216) 20%, rgb(240, 132, 169) 40%, rgb(163, 214, 240) 60%, rgb(215, 157, 206) 80%, rgb(159, 121, 160) 100%)",
    dark: "linear-gradient(135deg, rgb(29, 29, 68) 0%, rgb(89, 42, 133) 15%, rgb(122, 28, 79) 35%, rgb(20, 116, 148) 55%, rgb(79, 65, 108) 75%, rgb(73, 20, 111) 100%)",
  },
  golden: {
    light:
      "linear-gradient(135deg, rgb(240, 221, 164) 0%, rgb(240, 223, 209) 25%, rgb(240, 226, 201) 50%, rgb(240, 199, 164) 75%, rgb(240, 215, 167) 100%)",
    dark: "linear-gradient(135deg, rgb(97, 85, 20) 0%, rgb(97, 86, 76) 25%, rgb(97, 88, 74) 50%, rgb(97, 70, 20) 75%, rgb(97, 78, 22) 100%)",
  },
};

export const getBackground = (backgroundType, isDark = false) => {
  // Check if dark mode
  if (typeof document !== "undefined") {
    isDark = document.documentElement.classList.contains("dark");
  }

  // Get gradient background
  if (gradientBackgrounds[backgroundType]) {
    return gradientBackgrounds[backgroundType][isDark ? "dark" : "light"];
  }

  // Default: return CSS variable value
  if (typeof document !== "undefined") {
    const bgValue = getComputedStyle(document.documentElement)
      .getPropertyValue("--background")
      .trim();
    if (bgValue) {
      return `hsl(${bgValue})`;
    }
  }

  return "#EAEEF0"; // Fallback
};

/**
 * Custom hook that returns a function to get component background color with global state
 * @returns {Function} Function that takes themeColor and returns color with applied alpha
 */
export const useComponentBackgroundColor = () => {
  const backgroundType = useRecoilValue(backgroundTypeState);
  const backgroundOpacity = useRecoilValue(backgroundOpacityState);

  return useMemo(() => {
    return (themeColor) => {
      // Return themeColor if it's invalid or backgroundType is default
      if (!themeColor || backgroundType === "default") {
        return themeColor;
      }

      // If themeColor is not a string (e.g., it's already a gradient), return as is
      if (typeof themeColor !== "string") {
        return themeColor;
      }

      // Map opacity levels to alpha values
      const opacityMap = {
        low: 0.3,
        medium: 0.5,
        high: 0.7,
        full: 1,
      };

      const alphaValue = opacityMap[backgroundOpacity] || opacityMap.medium;

      // Only apply alpha if themeColor is a valid color string
      try {
        return alpha(themeColor, alphaValue);
      } catch (error) {
        console.warn("Failed to apply alpha to color:", themeColor, error);
        return themeColor;
      }
    };
  }, [backgroundType, backgroundOpacity]);
};

/**
 * Custom hook that properly responds to theme changes
 * This solves the issue where useMediaQuery doesn't refresh when theme breakpoints change
 */
export const useResponsiveMediaQuery = (queryFunction) => {
  const theme = useTheme();

  // Force re-evaluation when theme changes by including theme in dependency
  const query = useMemo(() => {
    if (typeof queryFunction === "function") {
      return queryFunction(theme);
    }
    return queryFunction;
  }, [queryFunction, theme, theme?.breakpoints?.values]);

  return useMediaQuery(query);
};

/**
 * Custom hook that provides a common way to determine if screen is small
 * Uses current theme breakpoints and window width for consistency
 */
export const useIsSmallScreen = () => {
  const theme = useTheme();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Default breakpoint if theme doesn't have breakpoints
  const mdBreakpoint = theme?.breakpoints?.values?.md || 960;

  return useMemo(() => {
    return windowWidth < mdBreakpoint;
  }, [windowWidth, mdBreakpoint]);
};

/**
 * Custom hook that determines if the left part of the screen is small
 * Considers the chat sidebar and only checks the available width on the left
 */
export const useIsLeftPartSmallScreen = () => {
  const theme = useTheme();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isChatSidebarVisible = useRecoilValue(chatSidebarVisibleState);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Default breakpoint if theme doesn't have breakpoints
  const mdBreakpoint = theme?.breakpoints?.values?.md || 960;

  return useMemo(() => {
    if (!isChatSidebarVisible) {
      // If sidebar is not visible, behave like normal useIsSmallScreen
      return windowWidth < mdBreakpoint;
    }

    // Calculate available width on the left (excluding chat sidebar)
    const sidebarWidth = 400; // Same as used in AdaptiveThemeProvider
    const leftPartWidth = windowWidth - sidebarWidth;

    // Check if the left part is small
    return leftPartWidth < mdBreakpoint;
  }, [windowWidth, mdBreakpoint, isChatSidebarVisible]);
};

/**
 * AdaptiveThemeProvider
 *
 * This provider wraps the main content area and dynamically adjusts Material-UI breakpoints
 * when the chat sidebar is visible. The main content components will respond as if they
 * have the full screen width, without needing any code changes.
 *
 * How it works:
 * - When sidebar is closed: Uses original breakpoints (996px for md, etc.)
 * - When sidebar is open: Scales down breakpoints proportionally based on available width
 *   Example: If sidebar takes 400px from 1400px screen, md breakpoint becomes 996 * (1000/1400) ≈ 711px
 */
const AdaptiveThemeProvider = ({ children }) => {
  const originalTheme = useTheme();

  // Check if theme has breakpoints (MUI theme) or use default breakpoints
  const defaultBreakpoints = {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  };
  const breakpoints = originalTheme?.breakpoints || defaultBreakpoints;

  // Create up function for media query
  const up = (key) => {
    const value = breakpoints.values[key] || defaultBreakpoints.values[key];
    return `@media (min-width:${value}px)`;
  };

  const isMdOrLgScreen = useMediaQuery(up("md"));

  // Track chat sidebar state from global state
  const isChatSidebarVisible = useRecoilValue(chatSidebarVisibleState);

  // Track current viewport width
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  // Listen for window resize changes
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Create adaptive theme with adjusted breakpoints
  const { adaptiveTheme, themeKey } = useMemo(() => {
    if (!isMdOrLgScreen || !isChatSidebarVisible) {
      return {
        adaptiveTheme: originalTheme,
        themeKey: "default",
      };
    }

    // Calculate the scale factor
    const sidebarWidth = 400; // Your sidebar width
    const availableWidth = viewportWidth - sidebarWidth;
    const scaleFactor = availableWidth / viewportWidth;

    // Get original breakpoint values
    const originalBreakpoints = breakpoints.values;

    // Scale down the breakpoints proportionally
    const adaptedBreakpoints = {
      xs: originalBreakpoints.xs,
      sm: originalBreakpoints.sm + sidebarWidth,
      md: originalBreakpoints.md + sidebarWidth,
      lg: originalBreakpoints.lg + sidebarWidth,
      xl: originalBreakpoints.xl + sidebarWidth,
    };

    // Create new theme with adapted breakpoints
    // Only create new theme if originalTheme is a full MUI theme
    if (originalTheme?.breakpoints) {
      const newTheme = createTheme({
        ...originalTheme,
        breakpoints: {
          ...originalTheme.breakpoints,
          values: adaptedBreakpoints,
        },
      });
      return {
        adaptiveTheme: newTheme,
        themeKey: `sidebar`,
      };
    }

    // If no breakpoints, just return original theme
    return {
      adaptiveTheme: originalTheme,
      themeKey: "default",
    };
  }, [
    originalTheme,
    isMdOrLgScreen,
    isChatSidebarVisible,
    viewportWidth,
    breakpoints,
  ]);

  // Only wrap with ThemeProvider if we have a valid MUI theme
  if (adaptiveTheme?.breakpoints) {
    return (
      <ThemeProvider theme={adaptiveTheme} key={themeKey}>
        {children}
      </ThemeProvider>
    );
  }

  // If no valid theme, just return children
  return <>{children}</>;
};

export default AdaptiveThemeProvider;
